import { useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "react-feather";

import { AppStrings } from "../../../../../../utils/constants/AppStrings";
import Select from "../../../../../shared/Select/Select";
import MonthlyHistoryTable from "./MonthlyHistoryTable";

const HISTORY_PREVIEW_LIMIT = 10;

/**
 * Collapsible wrapper around a monthly challenge history table (Pick / Casino /
 * Rebet / Referral / Follow). Keeps long tables from forcing admins to scroll
 * past them. When there are more than 10 rows, shows a local "See all" expand
 * (APIs are not paginated — FE-2429).
 *
 * Passing `searchPlaceholder` renders a search bar above the table (FE-2435:
 * Bet ID search on Pick History). Filtering is local and case-insensitive
 * against each row's `searchText`; the header keeps the unfiltered count so it
 * still describes the full data set.
 *
 * Passing `searchFilters` (`[{ key, label }]`) additionally renders a field
 * dropdown next to the search input (FE-2436: Rebet History). Rows then supply
 * a `searchValues` map keyed by those filter keys; "All" matches against every
 * value in the map, a specific field matches only that value.
 *
 * Passing `countedFilterLabel` adds a Select next to the search field
 * dropdown (Follower History: Counted for Current Month). The closed
 * control shows that label as the placeholder; the menu is All / Yes / No.
 */
const SEARCH_ALL_FIELDS = "all";
const COUNTED_FILTER_ALL = "all";
const COUNTED_FILTER_YES = "yes";
const COUNTED_FILTER_NO = "no";

const rowsMatchingCountedStatus = (rows, countedStatus) => {
  if (countedStatus === COUNTED_FILTER_YES) {
    return rows.filter((row) => row.counted === true);
  }
  if (countedStatus === COUNTED_FILTER_NO) {
    return rows.filter((row) => row.counted === false);
  }
  return rows;
};

const rowMatchesQuery = (row, query, searchField) => {
  const values =
    searchField === SEARCH_ALL_FIELDS
      ? Object.values(row.searchValues ?? { text: row.searchText ?? row.id })
      : [row.searchValues?.[searchField]];
  return values.some((value) =>
    String(value ?? "")
      .toLowerCase()
      .includes(query),
  );
};

const MonthlyHistorySection = ({
  label,
  columns = [],
  rows = [],
  emptyText,
  rowTestIdPrefix,
  sortable = false,
  defaultOpen = true,
  previewLimit = HISTORY_PREVIEW_LIMIT,
  searchPlaceholder,
  searchFilters,
  countedFilterLabel,
  legend,
  footnote,
  testId = "monthly-history-section",
}) => {
  const sectionRef = useRef(null);
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState(SEARCH_ALL_FIELDS);
  const [countedStatus, setCountedStatus] = useState(COUNTED_FILTER_ALL);
  const countableRows = rows.filter((row) => row?.excludeFromCount !== true);
  const rowCount = countableRows.length;
  const countedRows = countedFilterLabel
    ? rowsMatchingCountedStatus(rows, countedStatus)
    : rows;
  const query = searchPlaceholder ? searchQuery.trim().toLowerCase() : "";
  const filteredRows = query
    ? countedRows.filter((row) => rowMatchesQuery(row, query, searchField))
    : countedRows;
  const canExpand = filteredRows.length > previewLimit;
  const visibleRows =
    showAll || !canExpand ? filteredRows : filteredRows.slice(0, previewLimit);
  const title = rowCount > 0 ? `${label} (${rowCount})` : label;
  const hasActiveFilters =
    Boolean(query) ||
    (Boolean(countedFilterLabel) && countedStatus !== COUNTED_FILTER_ALL);
  const showToolbar =
    rowCount > 0 &&
    Boolean(searchPlaceholder || countedFilterLabel || legend);

  const handleToggleShowAll = () => {
    if (showAll) {
      setShowAll(false);
      // After the table collapses, keep this history section in view so the
      // admin does not land mid-page on a different challenge card.
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      });
      return;
    }
    setShowAll(true);
  };

  return (
    <div
      ref={sectionRef}
      className="user-manager-game-pass-monthly-history"
      data-testid={testId}
    >
      <button
        type="button"
        className="user-manager-game-pass-monthly-history-toggle"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={open ? AppStrings.COLLAPSE : AppStrings.EXPAND}
        data-testid={`${testId}-toggle`}
      >
        <span
          className="user-manager-game-pass-monthly-history-label"
          data-testid={`${testId}-label`}
        >
          {title}
        </span>
        <span
          className="user-manager-game-pass-monthly-history-chevron"
          aria-hidden="true"
        >
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open && (
        <div
          className="user-manager-game-pass-monthly-history-body"
          data-testid={`${testId}-body`}
        >
          {showToolbar && (
            <div className="user-manager-game-pass-monthly-history-search-row">
              {searchPlaceholder && (
                <input
                  type="search"
                  className="user-manager-game-pass-monthly-history-search"
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  data-testid={`${testId}-search`}
                />
              )}
              {/* Field dropdown sits to the right of the input, per design.
                  Shared platform Select (antd) so it matches every other
                  dropdown in PAM. */}
              {Array.isArray(searchFilters) && searchFilters.length > 0 && (
                <Select
                  className="user-manager-game-pass-monthly-history-search-field"
                  aria-label={AppStrings.SEARCH_BY}
                  value={searchField}
                  onChange={setSearchField}
                  options={[
                    { value: SEARCH_ALL_FIELDS, label: AppStrings.ALL },
                    ...searchFilters.map((filter) => ({
                      value: filter.key,
                      label: filter.label,
                    })),
                  ]}
                  data-testid={`${testId}-search-field`}
                />
              )}
              {countedFilterLabel && (
                <Select
                  className="user-manager-game-pass-monthly-history-counted-filter"
                  aria-label={countedFilterLabel}
                  placeholder={countedFilterLabel}
                  value={
                    countedStatus === COUNTED_FILTER_ALL ? null : countedStatus
                  }
                  onChange={(value) => {
                    setCountedStatus(value ?? COUNTED_FILTER_ALL);
                    setShowAll(false);
                  }}
                  options={[
                    { value: COUNTED_FILTER_ALL, label: AppStrings.ALL },
                    { value: COUNTED_FILTER_YES, label: AppStrings.YES },
                    { value: COUNTED_FILTER_NO, label: AppStrings.NO },
                  ]}
                  data-testid={`${testId}-counted-filter`}
                />
              )}
              {legend ? (
                <div
                  className="user-manager-game-pass-monthly-history-legend"
                  data-testid={`${testId}-legend`}
                >
                  {legend}
                </div>
              ) : null}
            </div>
          )}
          <MonthlyHistoryTable
            columns={columns}
            rows={visibleRows}
            emptyText={
              hasActiveFilters && rowCount > 0
                ? AppStrings.NO_RESULTS_FOUND
                : emptyText
            }
            rowTestIdPrefix={rowTestIdPrefix}
            sortable={sortable}
            testId={`${testId}-table`}
          />
          {canExpand && (
            <button
              type="button"
              className="user-manager-game-pass-monthly-history-see-all"
              onClick={handleToggleShowAll}
              aria-expanded={showAll}
              data-testid={
                showAll ? `${testId}-see-less` : `${testId}-see-all`
              }
            >
              <span>
                {showAll
                  ? AppStrings.USER_GAME_PASS_TASK_BETS_SEE_LESS
                  : AppStrings.SEE_ALL}
              </span>
              <span
                className="user-manager-game-pass-monthly-history-see-all-icon"
                aria-hidden="true"
                data-testid={
                  showAll
                    ? `${testId}-see-less-icon`
                    : `${testId}-see-all-icon`
                }
              >
                {showAll ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            </button>
          )}
          {footnote ? (
            <p
              className="user-manager-game-pass-monthly-history-footnote"
              data-testid={`${testId}-footnote`}
            >
              {footnote}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default MonthlyHistorySection;
